import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Timeline, ScrollTrigger } from 'react-gsap';
import MobileDetect from 'mobile-detect';
import Image from '../image';
import styles from './Section.module.scss';

function Section({ zIndex, debug, showTitlesOnSide, isLast, scrub, start, end, className, children }) {
  const [scrollTop, setScrollTop] = useState(0);
  const md = new MobileDetect(window.navigator.userAgent);
  const actualRef = useRef();
  const placeholderRef = useRef();
  const isMobile = md.mobile();
  const distance = isLast ? 0 : window.innerHeight / 2;

  useEffect(() => {
    const onScroll = () => {
      setScrollTop(window.scrollY);
    };

    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  let progress = 0;
  if (placeholderRef.current) {
    const base = placeholderRef.current.offsetTop - window.innerHeight;
    const current = scrollTop + distance;
    const target = placeholderRef.current.offsetTop;

    progress = (current - base) / (target - base);
    progress = Math.min(Math.max(0, progress), 1);
  }

  let top = 0;
  if (placeholderRef.current) {
    if (scrollTop >= placeholderRef.current.offsetTop) {
      // Start moving the section up
      top = placeholderRef.current.offsetTop - scrollTop;

      if (top + placeholderRef.current.clientHeight < 0) {
        // Section is offscreen
        top = -placeholderRef.current.clientHeight;
      }
    }
  }

  const subtitlesTop = [
    -top,
    window.innerHeight - top - 80 - Math.min((176 + 50) / 1920 * window.innerWidth, (176 + 50)),
  ];

  const body = (
    <div
      ref={actualRef}
      className={classnames(styles.section, isLast && styles.isLast, isMobile ? styles.mobile : styles.fixed, className)}
      style={{
        top: top + 80,
        zIndex,
      }}
    >
      {
        showTitlesOnSide && (
          <>
            <div
              className={styles.subtitle01}
              style={{
                top: actualRef.current ? Math.min(
                  subtitlesTop[0],
                  actualRef.current.clientHeight - window.innerHeight + 80
                ) : subtitlesTop[0],
              }}
            >
              <Image src="leah-dou/text/subtitle01.svg" />
            </div>
            <div
              className={styles.subtitle02}
              style={{
                top: actualRef.current ? Math.min(
                  subtitlesTop[1],
                  actualRef.current.clientHeight - Math.min((176 + 50) / 1920 * window.innerWidth, (176 + 50))
                ) : (
                  subtitlesTop[1]
                ),
              }}
            >
              <Image src="leah-dou/text/subtitle02.svg" />
            </div>
          </>
        )
      }
      {children}
    </div>
  );

  return (
    <>
      <ScrollTrigger start={start} end={end} scrub={!isMobile && scrub}>
        <div
          ref={placeholderRef}
          className={classnames(styles.section, isMobile && styles.mobile, styles.placeholder, className)}
          style={
            (!isMobile && actualRef.current) ? {
              height: `${actualRef.current.clientHeight + distance}px`,
            } : undefined
          }
        />
        {
          isMobile ? (
            body
          ) : (
            <Timeline
              playState="pause"
              progress={progress}
            >
              {body}
            </Timeline>
          )
        }
      </ScrollTrigger>
    </>
  );
}

Section.propTypes = {
  zIndex: PropTypes.number,
  debug: PropTypes.bool,
  showTitlesOnSide: PropTypes.bool,
  isLast: PropTypes.bool,
  scrub: PropTypes.bool,
  start: PropTypes.string,
  end: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

Section.defaultProps = {
  start: 'top bottom',
  end: 'top 25%',
};

export default Section;
